import { memo, useState } from "react";

import dayjs from "dayjs";
import { observer } from "mobx-react-lite";

import { IconDownload, IconViewer } from "@synthesis/shared-icons";
import { getApiBaseUrl } from "@synthesis/shared-services";
import { Checkbox } from "@synthesis/shared-ui";

import type { DocumentListResponse, FileMetaDto } from "../../../api";
import { Button, Tooltip } from "@synthesis/shared-ui";

import useProjectMeta from "../../../hooks/use-project-meta";
import { useStores } from "../../../store";
import { DragAndDropVersions } from "../../drag-and-drop-versions";
import ModalPreview from "../../files/local-components/modal-preview";
import { useVersions } from "../hooks";

type VersionProps = {
  data: DocumentListResponse;
  onCheckedFilesIFC: (fileMeta: FileMetaDto) => void;
  stateFunctions: { openViewer: (file?: FileMetaDto) => void };
};

const Version = observer<VersionProps>(({ data, onCheckedFilesIFC, stateFunctions }) => {
  const { fileStore } = useStores();
  const [check, setCheck] = useState(false);
  const fileMeta = data.fileMeta;

  if (!fileMeta) {
    return null;
  }

  const onClickCheck = () => {
    setCheck(!check);
    onCheckedFilesIFC(fileMeta);
  };

  const handleOpenViewer = () => {
    stateFunctions.openViewer(fileMeta);
  };

  return (
    <div className="mt-4 px-4">
      <div className="relative flex justify-between" data-cy="versionsIFC-versionBlock">
        <div className="flex items-center">
          <div>
            <Checkbox data-cy="versionsIFC-checkbox" onChange={onClickCheck} checked={check} />
          </div>
          <div className="ml-3 max-w-80">
            <h2 className="overflow-hidden overflow-ellipsis text-sm font-medium" data-cy="versionsIFC-title">
              {fileMeta.baseName}
            </h2>
            <p className="text-xs text-gray-500" data-cy="versionsIFC-description">
              {dayjs(fileMeta.modifiedAt).format("DD.MM.YYYY HH:mm:ss")} | {fileMeta.size} байт
            </p>
          </div>
        </div>

        <div className="flex cursor-pointer items-center">
          <Tooltip description="Открыть модель">
            <IconViewer data-cy="versionsIFC-iconViewer" onClick={handleOpenViewer} className="mr-2 h-[20px]" />
          </Tooltip>
          <Tooltip description="Скачать версию">
            <IconDownload
              data-cy="versionsIFC-iconDownload"
              onClick={() => {
                fileStore.downloadFile({
                  id: fileMeta.id,
                  baseName: fileMeta.baseName,
                  ext: fileMeta.ext ?? "",
                });
              }}
              className="size-6 text-[#eb0000]"
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

// eslint-disable-next-line @typescript-eslint/ban-types
type VersionsIFCProps = {
  info: unknown;
  selectedId: string;
  selectedFile: FileMetaDto & { projectId?: string };
  onClickModel: (data: FileMetaDto) => void;
  onUpdateVersion: (newFileId: string) => void;
  handleCloseModalMeta: () => void;
  stateFunctions: { openViewer: (file?: FileMetaDto) => void };
};

const VersionsIFC = memo<VersionsIFCProps>(({ stateFunctions, selectedId, selectedFile, onUpdateVersion }) => {
  const { isLoading, versions, postVersion } = useVersions(selectedId);
  const [selectedModels] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [checkedFiles, setCheckedFiles] = useState<FileMetaDto[]>([]);
  const { generalStore } = useStores();
  const { updateMetaVersion } = useProjectMeta(selectedFile.projectId ?? null);

  const handleClickEnter = () => {
    const files = checkedFiles
      .toSorted((a, b) => b.version - a.version)
      .map((el) => el.id)
      .join(",")
      .split(",");
    const url = `${getApiBaseUrl()}/viewer/#/${generalStore.projectId}/compare?current=${files[0]}&old=${files[1]}`;
    window.open(url, "_blank")?.focus();
  };

  const onCheckedFilesIFC = (fileMeta: FileMetaDto) => {
    if (checkedFiles.find((e) => e.id === fileMeta.id)) {
      setCheckedFiles((prevState) => {
        const newState = [...prevState];
        newState.splice(
          prevState.findIndex((e) => e.id === fileMeta.id),
          1,
        );
        return newState;
      });
    } else {
      setCheckedFiles((prevState) => [...prevState, fileMeta]);
    }
  };

  return (
    <main className="relative h-[93%] w-full overflow-y-scroll [&::-webkit-scrollbar-thumb]:rounded-[20px] [&::-webkit-scrollbar-thumb]:bg-[#d1d5db] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar]:rounded-lg">
      {!isLoading ? (
        versions?.map((data) => (
          <Version key={data.fileMeta?.id ?? ""} onCheckedFilesIFC={onCheckedFilesIFC} data={data} stateFunctions={stateFunctions} />
        ))
      ) : (
        <div className="px-4 pt-4">
          <div className="flex flex-wrap">
            {Array.from(Array(6), (_, i) => (
              <div key={i} className="bg-grey-300 mb-3 h-[40px] w-full animate-pulse rounded bg-opacity-30" />
            ))}
          </div>
        </div>
      )}

      {checkedFiles.length === 2 && (
        <Button data-cy="versionsIFC-button" className="ml-3 mt-2" onClick={handleClickEnter}>
          Сравнить
        </Button>
      )}

      <DragAndDropVersions
        onUpdateVersion={onUpdateVersion}
        updateMetaVersion={updateMetaVersion}
        selectedId={selectedId}
        postVersion={postVersion}
      />
      {isPreviewOpen && (
        <ModalPreview
          modelId={selectedModels}
          compare={true}
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          openModal={() => setIsPreviewOpen(true)}
          closeModal={() => setIsPreviewOpen(false)}
        />
      )}
    </main>
  );
});

export default VersionsIFC;
